export interface TalentDiagnosticsQuestionOption {
  key: string;
  text: string;
  score: number | '';
}

export interface TalentDiagnosticsQuestionFormValues {
  key: string;
  text: string;
  description: string;
  type: string;
  weighting: string;
  isRequired: boolean;
  options: TalentDiagnosticsQuestionOption[];
}

export const QUESTION_TYPES = ['Select', 'MultiSelect', 'Rating', 'Boolean', 'Text'] as const;

export const DEFAULT_QUESTION_TYPE = 'Select';

const RATING_OPTIONS = [20, 40, 60, 80, 100];

export const getDefaultQuestionOptions = (type: string): TalentDiagnosticsQuestionOption[] => {
  if (type !== 'Rating') {
    return [{ key: '', text: '', score: '' }];
  }

  return RATING_OPTIONS.map((score, index) => ({
    key: String(index + 1),
    text: '',
    score,
  }));
};

export const isOptionEditorVisible = (type: string) => ['Select', 'MultiSelect', 'Rating'].includes(type);

export const getQuestionOptionCount = (question: { options?: Array<{ key?: string; text?: string; score?: number | null }>; optionsJson?: string | null }) => {
  if (Array.isArray(question.options) && question.options.length > 0) {
    return question.options.length;
  }

  if (typeof question.optionsJson === 'string' && question.optionsJson.trim()) {
    try {
      const parsed = JSON.parse(question.optionsJson);
      return Array.isArray(parsed) ? parsed.length : 0;
    } catch {
      return 0;
    }
  }

  return 0;
};

export const validateQuestionForm = (form: Partial<TalentDiagnosticsQuestionFormValues>) => {
  if (!form.key?.trim() || !form.text?.trim()) {
    return { isValid: false, message: 'Question key and text are required.' };
  }

  if (form.type === 'Select' && (!Array.isArray(form.options) || form.options.length === 0)) {
    return { isValid: false, message: 'Select questions require at least one option.' };
  }

  if (Array.isArray(form.options)) {
    for (const option of form.options) {
      if (!option.text?.trim()) {
        return { isValid: false, message: 'Option text cannot be empty.' };
      }

      if (option.score === '' || option.score === null || option.score === undefined) {
        return { isValid: false, message: 'Option score is required.' };
      }
    }
  }

  return { isValid: true };
};

export const buildQuestionPayload = (values: {
  id?: string;
  sectionId: string;
  key: string;
  text: string;
  description: string;
  type: string;
  isRequired: boolean;
  weighting: number;
  options: TalentDiagnosticsQuestionOption[];
}) => {
  const normalizedOptions: Array<{ key: string; text: string; score?: number }> = (values.options || []).map((option) => {
    const normalizedScore = option.score === '' ? undefined : Number(option.score);
    return {
      key: option.key?.trim() || '',
      text: option.text?.trim() || '',
      ...(normalizedScore !== undefined ? { score: normalizedScore } : {}),
    };
  });

  return {
    id: values.id ?? undefined,
    sectionId: values.sectionId,
    key: values.key.trim(),
    text: values.text.trim(),
    description: values.description.trim(),
    type: values.type,
    isRequired: values.isRequired,
    weighting: Number(values.weighting || 0),
    options: normalizedOptions,
    optionsJson: JSON.stringify(normalizedOptions),
  };
};
